import { useState } from "react";

// ── All 6 statuses ────────────────────────────────────────────────
const STATUS = {
  full:     { color:"#059669", bg:"#D1FAE5", border:"#A7F3D0", label:"Full Day",  icon:"✓" },
  half:     { color:"#D97706", bg:"#FEF3C7", border:"#FDE68A", label:"Half Day",  icon:"½" },
  overtime: { color:"#0369A1", bg:"#DBEAFE", border:"#93C5FD", label:"Overtime",  icon:"⏱" },
  absent:   { color:"#DC2626", bg:"#FEF2F2", border:"#FECACA", label:"Absent",    icon:"✗" },
  holiday:  { color:"#B45309", bg:"#FEF3C7", border:"#FDE68A", label:"Holiday",   icon:"★" },
  leave:    { color:"#7C3AED", bg:"#EDE9FE", border:"#DDD6FE", label:"Leave",     icon:"✈" },
};

const WORKED = ["full","half","overtime"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AttendanceCalendar({ records = [] }) {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const pad    = n => String(n).padStart(2, "0");
  const getKey = d => `${year}-${pad(month+1)}-${pad(d)}`;

  // Map: "YYYY-MM-DD" → { status, overtimeHours }
  // Local date used (not UTC) to avoid IST timezone shift
  const dataMap = {};
  records.forEach(r => {
    const d = new Date(r.date);
    const key = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    dataMap[key] = { status: r.status, overtimeHours: r.overtimeHours };
  });

  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells       = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => setViewDate(new Date(year, month-1, 1));
  const nextMonth = () => setViewDate(new Date(year, month+1, 1));
  const goToday   = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  // Month stats
  const monthPrefix = `${year}-${pad(month+1)}`;
  const monthKeys   = Object.keys(dataMap).filter(k => k.startsWith(monthPrefix));
  const stats = { full:0, half:0, overtime:0, absent:0, holiday:0, leave:0 };
  monthKeys.forEach(k => { if (stats[dataMap[k].status] !== undefined) stats[dataMap[k].status]++; });
  const workedCount = monthKeys.filter(k => WORKED.includes(dataMap[k].status)).length;
  const pct = daysInMonth ? Math.round((workedCount / daysInMonth) * 100) : 0;

  const isToday = d => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .cal-wrap { font-family:'Plus Jakarta Sans',sans-serif; }
        .cal-day-cell { aspect-ratio:1; display:flex; flex-direction:column; align-items:center; justify-content:center; border-radius:10px; font-size:12px; font-weight:600; cursor:default; transition:transform .12s; position:relative; }
        .cal-day-cell.has-status { cursor:pointer; }
        .cal-day-cell.has-status:hover { transform:scale(1.1); z-index:2; }
        .cal-nav-btn { width:32px; height:32px; border-radius:9px; border:1.5px solid rgba(255,255,255,.3); background:rgba(255,255,255,.2); display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:16px; transition:all .15s; color:#fff; }
        .cal-nav-btn:hover { background:rgba(255,255,255,.35); }
        .today-btn { padding:5px 12px; border-radius:20px; border:1.5px solid rgba(255,255,255,.35); background:rgba(255,255,255,.2); font-family:'Plus Jakarta Sans',sans-serif; font-size:11px; font-weight:700; color:#fff; cursor:pointer; transition:all .15s; }
        .today-btn:hover { background:rgba(255,255,255,.35); }
        /* Stats scroll row */
        .stats-scroll { display:grid; grid-template-columns:repeat(6,1fr); gap:6px; }
        @media (max-width:480px) {
          .cal-day-cell { border-radius:6px; font-size:11px; }
          .stats-scroll { grid-template-columns:repeat(3,1fr); }
        }
      `}</style>

      <div className="cal-wrap" style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"20px", overflow:"hidden" }}>

        {/* HEADER */}
        <div style={{ background:"linear-gradient(135deg,#92400E,#B45309,#D97706)", padding:"18px 20px 14px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
            <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <div style={{ textAlign:"center" }}>
              <p style={{ fontSize:"18px", fontWeight:"800", color:"#fff", letterSpacing:"-0.3px" }}>{MONTHS[month]} {year}</p>
              <button className="today-btn" onClick={goToday} style={{ marginTop:"4px" }}>Today</button>
            </div>
            <button className="cal-nav-btn" onClick={nextMonth}>›</button>
          </div>

          {/* 6 stat mini-boxes */}
          <div className="stats-scroll">
            {Object.entries(STATUS).map(([key, s]) => (
              <div key={key} style={{ background:"rgba(255,255,255,.15)", borderRadius:"8px", padding:"7px 6px", textAlign:"center" }}>
                <p style={{ fontSize:"16px", fontWeight:"800", color:"#fff", lineHeight:"1" }}>{stats[key]||0}</p>
                <p style={{ fontSize:"9px", color:"rgba(255,255,255,.8)", fontWeight:"600", marginTop:"2px", lineHeight:"1.2" }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div style={{ padding:"10px 18px", background:"#FFF7ED", borderBottom:"1.5px solid #FDE68A", display:"flex", alignItems:"center", gap:"10px" }}>
          <span style={{ fontSize:"11px", fontWeight:"700", color:"#92400E", whiteSpace:"nowrap" }}>Worked {pct}%</span>
          <div style={{ flex:1, height:"6px", background:"#FDE68A", borderRadius:"99px", overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#92400E,#D97706)", borderRadius:"99px", transition:"width .5s ease" }} />
          </div>
          <span style={{ fontSize:"11px", color:"#B45309", fontWeight:"600", whiteSpace:"nowrap" }}>{workedCount}/{daysInMonth}d</span>
        </div>

        {/* GRID */}
        <div style={{ padding:"14px 16px 16px" }}>
          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"3px", marginBottom:"4px" }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign:"center", fontSize:"10px", fontWeight:"700", color:"#A8A29E", padding:"5px 0" }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"3px" }}>
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} />;

              const key  = getKey(day);
              const data = dataMap[key];
              const cfg  = data ? STATUS[data.status] : null;
              const isTod = isToday(day);
              const isFuture = new Date(year, month, day) > today;
              const showOTDot = data?.status === "overtime" && data?.overtimeHours;

              return (
                <div key={key}
                  className={`cal-day-cell ${cfg?"has-status":""}`}
                  title={cfg
                    ? `${day} ${MONTHS[month]}: ${cfg.label}${showOTDot?" ("+data.overtimeHours+"h)":""}`
                    : ""}
                  style={{
                    background: cfg ? cfg.bg : isTod ? "#FEF3C7" : "#FAFAF9",
                    border: isTod && !cfg
                      ? "2px solid #D97706"
                      : cfg ? `1.5px solid ${cfg.border}` : "1.5px solid transparent",
                    color: cfg ? cfg.color : isFuture ? "#C4B5A4" : "#44403C",
                    fontWeight: isTod ? "800" : "600",
                    opacity: isFuture && !cfg ? 0.45 : 1,
                  }}
                >
                  <span style={{ fontSize:"12px", lineHeight:"1" }}>{day}</span>
                  {cfg && (
                    <span style={{ fontSize:"9px", fontWeight:"800", lineHeight:"1", marginTop:"1px" }}>
                      {cfg.icon}{showOTDot ? data.overtimeHours+"h" : ""}
                    </span>
                  )}
                  {isTod && !cfg && (
                    <div style={{ width:"4px", height:"4px", borderRadius:"50%", background:"#D97706", marginTop:"2px" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* LEGEND — 3+3 two rows */}
        <div style={{ padding:"10px 18px 14px", borderTop:"1.5px solid #F5F5F4" }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"8px 16px", justifyContent:"center" }}>
            {Object.entries(STATUS).map(([key, s]) => (
              <div key={key} style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                <div style={{ width:"22px", height:"20px", borderRadius:"5px", background:s.bg, border:`1.5px solid ${s.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", color:s.color, fontWeight:"800" }}>
                  {s.icon}
                </div>
                <span style={{ fontSize:"11px", fontWeight:"600", color:"#78716C" }}>{s.label}</span>
              </div>
            ))}
            <div style={{ display:"flex", alignItems:"center", gap:"5px" }}>
              <div style={{ width:"22px", height:"20px", borderRadius:"5px", background:"#FEF3C7", border:"2px solid #D97706", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", color:"#D97706", fontWeight:"800" }}>•</div>
              <span style={{ fontSize:"11px", fontWeight:"600", color:"#78716C" }}>Today</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}